import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApp, useAsync } from '../lib/app-context';
import { Async, Badge, Card, Empty, Field, SelectField, TextField } from '../components/ui';
import { isSuperAdmin, type Difficulty, type ModuleCategory } from '../lib/types';

const CATEGORIES: ModuleCategory[] = ['Strength', 'Cardio', 'Mobility', 'Recovery', 'Flow'];
const DIFFICULTIES: Difficulty[] = ['Beginner', 'Intermediate', 'Advanced'];

export function Modules() {
    const { data, user } = useApp();
    const modules = useAsync(() => data.listModules(), [user?.id]);
    const [creating, setCreating] = useState(false);

    return (
        <>
            <div className="page-head">
                <div>
                    <h1>Modules</h1>
                    <p className="muted">
                        {isSuperAdmin(user)
                            ? 'Every module on the platform.'
                            : 'The modules you own. Ask a super admin to grant access to others.'}
                    </p>
                </div>
                {isSuperAdmin(user) && (
                    <button className="btn" onClick={() => setCreating(true)}>New module</button>
                )}
            </div>

            {creating && (
                <NewModuleForm
                    onCancel={() => setCreating(false)}
                    onCreated={() => { setCreating(false); modules.reload(); }}
                />
            )}

            <Async state={modules}>
                {(list) => list.length === 0 ? (
                    <Empty
                        title="No modules yet"
                        hint={isSuperAdmin(user)
                            ? 'Create the first one to start authoring content.'
                            : 'You do not own any modules yet.'}
                    />
                ) : (
                    <div className="list">
                        {list.map((module) => (
                            <Link
                                key={module.id}
                                to={`/modules/${module.id}`}
                                className="list-row"
                                style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="title">{module.studio_name}</div>
                                    <div className="small muted">{module.tagline || module.slug}</div>
                                </div>
                                <Badge>{module.category}</Badge>
                                <Badge tone="teal">{module.difficulty}</Badge>
                                {module.live_version_id
                                    ? <Badge tone="teal">Published</Badge>
                                    : <Badge tone="coral">Draft</Badge>}
                            </Link>
                        ))}
                    </div>
                )}
            </Async>
        </>
    );
}

function NewModuleForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
    const { data } = useApp();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [studio, setStudio] = useState('');
    const [category, setCategory] = useState<ModuleCategory>('Strength');
    const [difficulty, setDifficulty] = useState<Difficulty>('Beginner');
    const [error, setError] = useState<string | null>(null);

    const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    async function create() {
        try {
            const module = await data.createModule({
                slug, name: name.trim(),
                studio_name: studio.trim() || `${name.trim()} Studio`,
                category, difficulty,
            });
            onCreated();
            navigate(`/modules/${module.id}`);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }

    return (
        <div style={{ marginBottom: 20 }}>
            <Card>
                <h2>New module</h2>
                <div className="stack" style={{ marginTop: 12 }}>
                    {error && <div className="banner error">{error}</div>}
                    <div className="field-row">
                        <TextField label="Name" value={name} onChange={setName} placeholder="Kettlebell" />
                        <TextField label="Studio name" value={studio} onChange={setStudio} placeholder="Kettlebell Forge" />
                    </div>
                    <div className="field-row">
                        <SelectField label="Category" value={category} onChange={setCategory}
                            options={CATEGORIES.map((c) => ({ value: c, label: c }))} />
                        <SelectField label="Difficulty" value={difficulty} onChange={setDifficulty}
                            options={DIFFICULTIES.map((d) => ({ value: d, label: d }))} />
                    </div>
                    <Field label="Slug">
                        <input value={slug} disabled />
                    </Field>
                    <div className="row">
                        <button className="btn" disabled={!slug} onClick={create}>Create</button>
                        <button className="btn secondary" onClick={onCancel}>Cancel</button>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export function ModuleOverview() {
    const { moduleId } = useParams();
    const { data } = useApp();
    const state = useAsync(async () => {
        const [module, exercises, sessions, skills, studies] = await Promise.all([
            data.getModule(moduleId!),
            data.listExercises(moduleId!),
            data.listSessions(moduleId!),
            data.listSkills(moduleId!),
            data.listSkillStudies(moduleId!),
        ]);
        return { module, counts: { exercises: exercises.length, sessions: sessions.length,
            skills: skills.length, studies: studies.length } };
    }, [moduleId]);

    return (
        <Async state={state}>
            {({ module, counts }) => {
                if (!module) return <Empty title="Module not found" hint="It may have been deleted, or you may not have access." />;
                return <OverviewForm key={module.id} module={module} counts={counts} onSaved={state.reload} />;
            }}
        </Async>
    );
}

function OverviewForm({ module, counts, onSaved }: {
    module: import('../lib/types').Module;
    counts: { exercises: number; sessions: number; skills: number; studies: number };
    onSaved: () => void;
}) {
    const { data } = useApp();
    const [form, setForm] = useState(module);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const dirty = JSON.stringify(form) !== JSON.stringify(module);

    async function save() {
        setSaving(true); setError(null);
        try {
            await data.updateModule(module.id, {
                name: form.name, studio_name: form.studio_name, tagline: form.tagline,
                parameters: form.parameters, category: form.category,
                difficulty: form.difficulty, icon: form.icon,
            });
            onSaved();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            <div className="page-head">
                <div>
                    <div className="eyebrow">Module</div>
                    <h1>{module.studio_name}</h1>
                </div>
                <div className="row">
                    {module.live_version_id ? <Badge tone="teal">Published</Badge> : <Badge tone="coral">Never published</Badge>}
                    <button className="btn" disabled={!dirty || saving} onClick={save}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>

            {error && <div className="banner error" style={{ marginBottom: 16 }}>{error}</div>}

            <div className="row" style={{ marginBottom: 16 }}>
                <Card tight><strong>{counts.exercises}</strong> <span className="muted small">exercises</span></Card>
                <Card tight><strong>{counts.sessions}</strong> <span className="muted small">sessions</span></Card>
                <Card tight><strong>{counts.studies}</strong> <span className="muted small">skill studies</span></Card>
                <Card tight><strong>{counts.skills}</strong> <span className="muted small">skill nodes</span></Card>
            </div>

            <Card>
                <h2>Details</h2>
                <div className="stack" style={{ marginTop: 12 }}>
                    <div className="field-row">
                        <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                        <TextField label="Studio name" value={form.studio_name} onChange={(v) => setForm({ ...form, studio_name: v })} />
                    </div>
                    <TextField label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
                    <TextField label="Module parameters" multiline value={form.parameters}
                        onChange={(v) => setForm({ ...form, parameters: v })} />
                    <div className="field-row-3">
                        <SelectField label="Category" value={form.category}
                            onChange={(v) => setForm({ ...form, category: v })}
                            options={CATEGORIES.map((c) => ({ value: c, label: c }))} />
                        <SelectField label="Difficulty" value={form.difficulty}
                            onChange={(v) => setForm({ ...form, difficulty: v })}
                            options={DIFFICULTIES.map((d) => ({ value: d, label: d }))} />
                        <TextField label="Icon (SF Symbol)" value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} />
                    </div>
                    <Field label="Slug">
                        <input value={form.slug} disabled />
                    </Field>
                    <p className="small muted">
                        The slug is fixed after creation — published snapshots and athlete progress
                        reference it.
                    </p>
                </div>
            </Card>
        </>
    );
}
